import { useAtomValue } from 'jotai';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Collapse,
  Divider,
  Typography,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import MailOutlineIcon from '@mui/icons-material/MailOutline';

import { navbarHeightAtom } from '../utils/AtomConfig';
import { AuthService } from '../utils/AuthService';
import Constants from '../utils/Constants';
import { SharePointService } from '../utils/SharePointService';

const LOGO_PATH = './logo-2.png';
const MICROSOFT_LOGO_PATH = './microsoft-logo.png';
const GOOGLE_LOGO_PATH = './google-logo.png';
const WHC_LOGO_PATH = './Whitehaven_Symbol_Dark-Blue_RGB.png';

const useStyles = makeStyles({
  sop: {
    minHeight: '100vh',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    backgroundColor: '#e8ecf1',
    paddingBottom: '20px',
    paddingLeft: '20px',
    paddingRight: '20px',
  },
  logo: {
    width: 100,
    margin: '20px 0px',
  },
  textContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
  },
  button: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    img: {
      height: 24,
    },
    span: {
      marginLeft: 10,
      marginTop: 5,
    },
  },
  securityNotice: {
    marginTop: 20,
    padding: 15,
    backgroundColor: '#f5f5f5',
    borderRadius: 8,
    border: '1px solid #e0e0e0',
    fontSize: 12,
    lineHeight: 1.4,
    color: '#333',
    '& h4': {
      margin: '0 0 10px 0',
      fontSize: 14,
      fontWeight: 'bold',
      color: '#000',
    },
    '& p': {
      margin: '0 0 8px 0',
    },
    '& ul': {
      margin: '5px 0 10px 0',
      paddingLeft: 20,
    },
    '& li': {
      marginBottom: 3,
    },
  },
});

export function Login() {
  const classes = useStyles();
  const sharePointService = SharePointService.Instance;
  const authService = AuthService.Instance;

  const navbarHeight = useAtomValue(navbarHeightAtom);
  const [bannerExpanded, setBannerExpanded] = useState(true);

  const { data: authSettings } = useQuery(
    Constants.QUERY_KEYS.AUTH_SETTINGS,
    () => sharePointService.getAuthSettings(),
    {
      ...Constants.BASE_DISABLE_REFETCH_SETTINGS,
    },
  );

  useEffect(() => {
    authService.removeExpirationTime();
  }, [authService]);

  const isSessionTimedOut = authService.checkIsSessionTimedOut();

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: '#e8ecf1' }}>
      <Box sx={{ pt: `${navbarHeight + 20}px`, px: '20px', display: 'flex', justifyContent: 'center' }}>
        <Box sx={{ maxWidth: 600, width: '100%' }}>
          {isSessionTimedOut && (
            <Alert severity='warning' sx={{ mb: 2 }}>
              <div>
                You've been signed out due to inactivity for security reasons. Please sign in again to continue.
              </div>
            </Alert>
          )}
          <Alert severity='info'>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 'bold', flex: 1, pt: '2px' }}>
                Attention QLD Contractors: Changes to the way you Login to Whitehaven Systems
              </Typography>
              <Button
                size='small'
                sx={{ minWidth: 'fit-content', ml: 1, whiteSpace: 'nowrap' }}
                onClick={() => setBannerExpanded(!bannerExpanded)}
              >
                {bannerExpanded ? 'Show less' : 'Read more'}
              </Button>
            </Box>
            <Collapse in={bannerExpanded}>
              <Typography variant='body2' sx={{ mb: 1, mt: 1 }}>
                Whitehaven is gradually transitioning QLD labour hire contractors to no longer use a <strong>whitehavencoal.com.au</strong> account to login to WIC. Instead, a personal email address recorded in Intelics must be used when logging in. If your recorded email is a work / business address, please ensure this is updated to a personal email address so that you can successfully login.
              </Typography>
              <Typography variant='body2' sx={{ mb: 1 }}>
                If you have issues signing-in with another option that you have previously used you will have been recently affected by this change. Once impacted, select <strong>QLD Contractors</strong> and enter your own Intelics recorded email address, click <strong>Forgot password</strong> to reset your password to set up one of your choosing, and authenticate with a code sent to your email inbox, this will then become your way to access WIC moving forward.
              </Typography>
              <Typography variant='body2'>
                For further instructions on logging in to WIC for the first time as a QLD Contractor please refer to our <a target='_blank' rel='noreferrer' href={"/QLD%20Contractor%20-%20Whitehaven%20Logon%20-%20Quick%20Reference%20Guide.pdf"}>logon Quick Reference Guide</a>.
              </Typography>
            </Collapse>
          </Alert>
        </Box>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', px: '20px', py: '20px' }}>
        <Box sx={{ maxWidth: 600, width: '100%' }}>
          <Card>
          <CardContent>
            <div className={classes.textContainer}>
              <img className={classes.logo} src={LOGO_PATH} alt='Logo'></img>
              <Typography
                sx={{ fontSize: 30, fontWeight: 900, color: 'black' }}
              >
                Welcome
              </Typography>
              <Typography sx={{ fontSize: 14, color: '#aeaeae' }} gutterBottom>
                Sign in with your registered email address.
              </Typography>
            </div>
            <Box sx={{ mt: 4, mb: 2 }}>
              {/* Section 1 */}
              <Divider textAlign='left' sx={{ mb: 2 }}>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  fontWeight='bold'
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  QLD Contractors
                </Typography>
              </Divider>

              <Button
                variant='outlined'
                fullWidth
                onClick={() => authService.loginMsal()}
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  mb: 2,
                  p: 1.5,
                  borderColor: '#c4c4c4',
                  backgroundColor: '#f5f7fa',
                  textTransform: 'none',
                  color: 'text.primary',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#eef3f8',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 2,
                  }}
                >
                  <MailOutlineIcon sx={{ color: '#757575', fontSize: 28 }} />
                </Box>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 'bold', lineHeight: 1.3 }}
                  >
                    QLD Contractors
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    Sign in with an enrolled external email address
                  </Typography>
                </Box>
              </Button>

              {/* Section 2 */}
              <Divider textAlign='left' sx={{ mb: 2, mt: 2 }}>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  fontWeight='bold'
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  NSW Contractors & Other Third-Party Providers
                </Typography>
              </Divider>

              <Button
                variant='outlined'
                fullWidth
                onClick={() =>
                  (window.location.href = `/.auth/login/aad?post_login_redirect_uri=${window.location.origin}`)
                }
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  mb: 2,
                  p: 1.5,
                  borderColor: '#c4c4c4',
                  backgroundColor: '#f5f7fa',
                  textTransform: 'none',
                  color: 'text.primary',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#eef3f8',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 2,
                  }}
                >
                  <img
                    src={MICROSOFT_LOGO_PATH}
                    alt='Microsoft'
                    style={{ height: 24 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 'bold', lineHeight: 1.3 }}
                  >
                    Sign in with Microsoft
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    Personal/Business - will authenticate on your home tenant
                  </Typography>
                </Box>
              </Button>

              <Button
                variant='outlined'
                fullWidth
                onClick={() =>
                  (window.location.href = `/.auth/login/google?post_login_redirect_uri=${window.location.origin}`)
                }
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  mb: 1,
                  p: 1.5,
                  borderColor: '#c4c4c4',
                  backgroundColor: '#f5f7fa',
                  textTransform: 'none',
                  color: 'text.primary',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#eef3f8',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 2,
                  }}
                >
                  <img
                    src={GOOGLE_LOGO_PATH}
                    alt='Google'
                    style={{ height: 24 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 'bold', lineHeight: 1.3 }}
                  >
                    Sign in with Google
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    Personal/Business - will authenticate on Google
                  </Typography>
                </Box>
              </Button>

              {/* Section 3 */}
              <Divider textAlign='left' sx={{ mb: 2, mt: 2 }}>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  fontWeight='bold'
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  Whitehaven Internal
                </Typography>
              </Divider>

              <Button
                variant='outlined'
                fullWidth
                onClick={() =>
                  (window.location.href = `/.auth/login/aad?post_login_redirect_uri=${window.location.origin}`)
                }
                sx={{
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  mb: 2,
                  p: 1.5,
                  borderColor: '#c4c4c4',
                  backgroundColor: '#f5f7fa',
                  textTransform: 'none',
                  color: 'text.primary',
                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                  '&:hover': {
                    borderColor: 'primary.main',
                    backgroundColor: '#eef3f8',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  },
                }}
              >
                <Box
                  sx={{
                    width: 48,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    mr: 2,
                  }}
                >
                  <img
                    src={WHC_LOGO_PATH}
                    alt='Whitehaven'
                    style={{ height: 24 }}
                  />
                </Box>
                <Box>
                  <Typography
                    variant='subtitle2'
                    sx={{ fontWeight: 'bold', lineHeight: 1.3 }}
                  >
                    Whitehaven Internal
                  </Typography>
                  <Typography
                    variant='caption'
                    color='textSecondary'
                    sx={{ display: 'block', lineHeight: 1.2 }}
                  >
                    Sign in with a Whitehaven email address
                  </Typography>
                </Box>
              </Button>
            </Box>
            <Divider sx={{ mb: 3 }} />
            <div style={{ marginTop: 10, textAlign: 'center' }}>
              <span style={{ fontSize: 14, color: '#aeaeae', marginRight: 4 }}>
                Don't have registered account?
              </span>
              <a
                target='_blank'
                rel='noreferrer'
                style={{ fontSize: 16, fontWeight: 600 }}
                href={authSettings?.registrationFormUrl}
              >
                Sign up here
              </a>
            </div>

            <div className={classes.securityNotice}>
              <h4>
                <span role='img' aria-label='Locked padlock'>
                  🔐
                </span>{' '}
                Website Access Notice – Acceptable Use & Security Policy
              </h4>
              <p>
                By logging into this website, you acknowledge and agree to the
                following:
              </p>

              <p>
                <strong>
                  <span role='img' aria-label='Check mark'>
                    ✅
                  </span>{' '}
                  Acceptable Use
                </strong>
              </p>
              <ul>
                <li>
                  Access is restricted to <strong>authorized users</strong> for{' '}
                  <strong>business purposes only</strong>.
                </li>
                <li>
                  You must comply with the company's{' '}
                  <strong>Acceptable Use Policy</strong>, which prohibits:
                  <ul>
                    <li>Unauthorized access or sharing of credentials.</li>
                    <li>
                      Distribution of confidential or sensitive information.
                    </li>
                    <li>
                      Use of the system for personal, illegal, or non-Whitehaven
                      business-related activities.
                    </li>
                  </ul>
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='Locked padlock'>
                    🔒
                  </span>{' '}
                  Security Protocols
                </strong>
              </p>
              <ul>
                <li>
                  This website uses <strong>secure encryption</strong> to
                  protect your session.
                </li>
                <li>
                  You are responsible for maintaining the{' '}
                  <strong>confidentiality of your login credentials</strong>.
                </li>
                <li>
                  Any attempt to <strong>bypass security controls</strong> or
                  access unauthorized data is strictly prohibited and may result
                  in the removal of access.
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='Warning sign'>
                    ⚠️
                  </span>{' '}
                  Monitoring & Compliance
                </strong>
              </p>
              <ul>
                <li>
                  All activity on this system is <strong>logged</strong> for
                  security and compliance.
                </li>
                <li>
                  <strong>Suspicious behaviour</strong> may trigger an
                  investigation and be reported to Whitehaven Cyber Security.
                </li>
              </ul>

              <p>
                <strong>
                  <span role='img' aria-label='No entry sign'>
                    🚫
                  </span>{' '}
                  Consequences of Misuse
                </strong>
              </p>
              <ul>
                <li>
                  Violations of these policies may result in{' '}
                  <strong>disciplinary action</strong>, including termination.
                </li>
                <li>
                  <strong>Unauthorized access</strong> or misuse may lead to{' '}
                  <strong>legal consequences</strong> and financial liability.
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </Box>
    </Box>
    </Box>
  );
}
